import { useEffect, useRef, useState } from 'react';

import { DateTime } from 'luxon';

import { MAX_LESSONS, TIME_ZONE } from '@common/constants/academyReworkings';
import {
	assignments,
	bookingPayload,
	canBook,
	hasBookingConflict,
	overlaps,
	parseDate,
} from '@common/helpers/academyReworkings';
import getRequestError from '@common/helpers/getReworkingRequestError';
import useModel from '@common/helpers/useModel';
import StudentLessons from '@common/models/AcademyReworkings/StudentLessons';
import StudentMeets from '@common/models/AcademyReworkings/StudentMeets';
import StudentSlots from '@common/models/AcademyReworkings/StudentSlots';
import {
	BookingAssignment,
	MissedLesson,
	SelectedTime,
	TimeWindow,
} from '@common/types/academyReworkings';

import { BookingProps, Step } from '../types';

export default function useBookingFlow(
	props: BookingProps,
	fallbackError: string,
	expiredError: string,
) {
	const firstLocationId = props.locations[0]?.id ?? null;
	const [locationId, setLocationId] = useState<number | null>(firstLocationId);
	const [groupId, setGroupId] = useState<number | null>(
		props.groups.find((group) => group.locationId === firstLocationId)?.id ?? null,
	);
	const [programId, setProgramId] = useState<number | null>(null);
	const [selectedLessons, setSelectedLessons] = useState<MissedLesson[]>([]);
	const [times, setTimes] = useState<SelectedTime[]>([]);
	const [selectedDays, setSelectedDays] = useState<string[]>([]);
	const [booked, setBooked] = useState<BookingAssignment[]>([]);
	const [step, setStep] = useState<Step>('home');
	const [week, setWeek] = useState(DateTime.now().setZone(TIME_ZONE).startOf('week').toISODate());
	const [phone, setPhone] = useState(props.phone || '');
	const [comment, setComment] = useState('');
	const [attempted, setAttempted] = useState(false);
	const [tutorsAttempted, setTutorsAttempted] = useState(false);
	const [busy, setBusy] = useState(false);
	const [catalogueError, setCatalogueError] = useState<string | null>(null);
	const [slotsError, setSlotsError] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [catalogueRevision, setCatalogueRevision] = useState(0);
	const [slotsRevision, setSlotsRevision] = useState(0);
	const submitting = useRef(false);
	const catalogue = useModel(
		StudentLessons,
		{ studentId: props.studentId || 0, groupId: groupId || 0 },
		[props.studentId, groupId, catalogueRevision],
		{ disableFetch: true },
	);
	const lessonList = useModel(
		StudentLessons,
		{ studentId: props.studentId || 0, groupId: groupId || 0 },
		[props.studentId, groupId, programId],
		{ disableFetch: true },
	);
	const slotList = useModel(
		StudentSlots,
		{ groupId: groupId || 0, programId: programId || 0, locationId: locationId || 0 },
		[groupId, programId, locationId],
		{ disableFetch: true },
	);
	const meetList = useModel(
		StudentMeets,
		{ studentId: props.studentId || 0, includeAllTypes: true },
		[props.studentId],
		{ disableFetch: true },
	);
	const programs = Array.from(
		new Map(catalogue.data.programs.map((program) => [program.program_id, program])).values(),
	);
	const lessons = lessonList.data.lessons.filter((lesson) => lesson.program_id === programId);
	const { windowsByWeek } = slotList;
	const existingBookings = meetList.data;
	const programsLoading = catalogue.loading;
	const slotsLoading = slotList.loading || meetList.loading;
	const errorMessage = (err: unknown) => getRequestError(err, fallbackError);
	const requestedWeeks =
		step === 'times' && selectedDays.length
			? Array.from(
					new Set(
						selectedDays.map((day) =>
							DateTime.fromISO(day, { zone: TIME_ZONE }).startOf('week').toISODate(),
						),
					),
				)
					.sort()
					.join(',')
			: week;

	useEffect(() => {
		let active = true;
		setCatalogueError(null);
		if (props.studentId && groupId) {
			catalogue.fetch().catch((err) => {
				if (active) setCatalogueError(errorMessage(err));
			});
		}
		return () => {
			active = false;
		};
	}, [catalogue]);

	useEffect(() => {
		if (
			!['days', 'times'].includes(step) ||
			!groupId ||
			!programId ||
			!locationId ||
			!props.studentId
		) return;
		let active = true;
		setSlotsError(null);
		const weeks = requestedWeeks.split(',');
		Promise.all([slotList.fetch(weeks), meetList.fetch()])
			.then(([results, studentBookings]) => {
				if (!active) return;
				const fresh = results.flatMap((result) => result.windows);
				setTimes((previous) =>
					previous.flatMap((time) => {
						if (hasBookingConflict(time.window, studentBookings)) return [];
						const timeWeek = parseDate(time.window.start).startOf('week').toISODate();
						if (!weeks.includes(timeWeek)) return [time];
						const replacement = fresh.find((item) => item.key === time.window.key);
						if (!replacement) return [];
						const selectedStillAvailable = replacement.slots.some(
							(slot) => slot.id === time.slotId,
						);
						const fallbackSlotId = replacement.slots[0]?.id ?? null;
						return [
							{
								window: replacement,
								slotId: selectedStillAvailable ? time.slotId : fallbackSlotId,
							},
						];
					}),
				);
			})
			.catch((err) => {
				if (active) setSlotsError(errorMessage(err));
			});
		return () => {
			active = false;
		};
	}, [step, slotList, meetList, requestedWeeks, slotsRevision]);

	const windows = Object.values(windowsByWeek).flat();
	const availableWindows = windows.filter(
		(window) =>
			canBook(window) &&
			!hasBookingConflict(window, existingBookings) &&
			!booked.some(({ slot }) => overlaps({ start: slot.start_dt, end: slot.end_dt }, window)),
	);

	const pendingLessons = selectedLessons.filter(
		(lesson) => !booked.some((item) => item.lesson.gradebook_id === lesson.gradebook_id),
	);
	const pendingTimes = times.filter((time) => !booked.some((item) => item.slot.id === time.slotId));
	const pendingAssignments = assignments(pendingLessons, pendingTimes);

	const changeLocation = (id: number | null) => {
		setLocationId(id);
		setGroupId(props.groups.find((group) => group.locationId === id)?.id ?? null);
		setProgramId(null);
		setAttempted(false);
	};
	const changeGroup = (id: number | null) => {
		setGroupId(id);
		setProgramId(null);
		setAttempted(false);
	};

	const startBooking = async () => {
		setAttempted(true);
		if (
			busy ||
			!props.studentId ||
			!locationId ||
			!groupId ||
			!programId ||
			!programs.some((program) => program.program_id === programId)
		) return;
		setBusy(true);
		setError(null);
		try {
			await lessonList.fetch(programId);
			setSelectedLessons([]);
			setTimes([]);
			setSelectedDays([]);
			slotList.clear();
			setBooked([]);
			setTutorsAttempted(false);
			setStep('lessons');
		} catch (err) {
			setError(errorMessage(err));
		} finally {
			setBusy(false);
		}
	};

	const toggleLesson = (lesson: MissedLesson) => {
		setSelectedLessons((current) => {
			if (current.some((item) => item.gradebook_id === lesson.gradebook_id)) {
				return current.filter((item) => item.gradebook_id !== lesson.gradebook_id);
			}
			return current.length < MAX_LESSONS ? [...current, lesson] : current;
		});
		setTimes([]);
		setSelectedDays([]);
	};

	const toggleDay = (day: string) => {
		if (selectedDays.includes(day)) {
			setSelectedDays((current) => current.filter((item) => item !== day));
			setTimes((current) =>
				current.filter((time) => parseDate(time.window.start).toISODate() !== day),
			);
		} else if (availableWindows.some((window) => parseDate(window.start).toISODate() === day)) {
			setSelectedDays((current) => [...current, day].sort());
		}
	};

	const toggleTime = (window: TimeWindow) => {
		setTimes((current) => {
			const pending = current.filter(
				(time) => !booked.some((item) => item.slot.id === time.slotId),
			);
			if (pending.some((time) => time.window.key === window.key)) {
				return pending.filter((time) => time.window.key !== window.key);
			}
			if (!canBook(window) || !selectedDays.includes(parseDate(window.start).toISODate())) {
				return current;
			}
			if (
				pending.length >= pendingLessons.length ||
				pending.some((time) => overlaps(time.window, window))
			) return current;
			const overlapsBooked = booked.some(({ slot }) =>
				overlaps({ start: slot.start_dt, end: slot.end_dt }, window),
			);
			if (overlapsBooked || hasBookingConflict(window, existingBookings)) return current;
			return [...pending, { window, slotId: window.slots[0]?.id ?? null }];
		});
	};
	const selectTutor = (key: string, slotId: number) => {
		setTimes((current) =>
			current.map((time) =>
				time.window.key === key && time.window.slots.some((slot) => slot.id === slotId)
					? { ...time, slotId }
					: time,
			),
		);
	};

	const showConfirmation = () => {
		setTutorsAttempted(true);
		if (pendingAssignments.length !== pendingLessons.length || !pendingLessons.length) return;
		setError(null);
		setStep('confirm');
	};

	const confirmBooking = async () => {
		if (
			submitting.current ||
			!props.studentId ||
			!locationId ||
			!groupId ||
			!programId ||
			pendingAssignments.length !== pendingLessons.length ||
			!pendingAssignments.length
		) return;
		submitting.current = true;
		setBusy(true);
		setError(null);
		try {
			const weeks = Array.from(
				new Set(
					pendingAssignments.map((item) =>
						parseDate(item.slot.start_dt).startOf('week').toISODate(),
					),
				),
			);
			const [slotPages, studentBookings] = await Promise.all([
				slotList.fetch(weeks),
				meetList.fetch(),
			]);
			const fresh = slotPages.flatMap((page) => page.slots);
			const stillFree = pendingAssignments.every(
				({ slot }) =>
					!hasBookingConflict({ start: slot.start_dt, end: slot.end_dt }, studentBookings) &&
					fresh.some(
						(item) =>
							item.id === slot.id &&
							item.status === 0 &&
							item.teacher?.id === slot.teacher.id &&
							parseDate(item.start_dt).toMillis() === parseDate(slot.start_dt).toMillis(),
					) &&
					canBook({ start: slot.start_dt }),
			);
			if (!stillFree) {
				setTimes((current) =>
					current.filter((time) => !hasBookingConflict(time.window, studentBookings)),
				);
				setError(expiredError);
				setStep('times');
				return;
			}
			for (let index = 0; index < pendingAssignments.length; index++) {
				const item = pendingAssignments[index];
				// Separate requests: /meets/bulk is for consultations, not working_off.
				// eslint-disable-next-line no-await-in-loop
				await meetList.add(
					bookingPayload(item, { studentId: props.studentId, locationId, phone, comment }),
				);
				setBooked((current) => [...current, item]);
			}
			setStep('bookings');
			try {
				await props.onBooked?.();
			} catch (err) {
				console.error(err);
			}
		} catch (err) {
			setError(errorMessage(err));
		} finally {
			setBusy(false);
			submitting.current = false;
		}
	};

	const back = () => {
		if (busy) return;
		setError(null);
		const previous: Partial<Record<Step, Step>> = {
			lessons: 'home',
			days: booked.length ? 'home' : 'lessons',
			times: 'days',
			tutors: 'times',
			confirm: 'tutors',
			bookings: 'home',
		};
		setStep(previous[step] || 'home');
	};

	return {
		...props,
		step,
		setStep,
		locationId,
		groupId,
		programId,
		setProgramId,
		programs,
		lessons,
		selectedLessons,
		pendingLessons,
		times: pendingTimes,
		selectedDays,
		toggleDay,
		pendingAssignments,
		booked,
		windows,
		availableWindows,
		week: DateTime.fromISO(week, { zone: TIME_ZONE }),
		changeWeek: (value: DateTime) => {
			if (value.isValid) setWeek(value.startOf('week').toISODate());
		},
		phone,
		setPhone,
		comment,
		setComment,
		busy,
		programsLoading,
		slotsLoading,
		catalogueError,
		slotsError,
		error,
		setError,
		attempted,
		tutorsAttempted,
		changeLocation,
		changeGroup,
		startBooking,
		toggleLesson,
		toggleTime,
		selectTutor,
		showConfirmation,
		confirmBooking,
		back,
		retryCatalogue: () => setCatalogueRevision((value) => value + 1),
		retrySlots: () => setSlotsRevision((value) => value + 1),
	};
}

export type BookingFlow = ReturnType<typeof useBookingFlow>;
